import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SageIntegrationService } from '../../../shared/services/sage-integration.service';
import { ToastService } from '../../shared/services/toast.service';
import {
  aivorRecordDisplay,
  aivorRecordIdFor,
  BOMComponentWithSageInfo,
  effectiveAivorRecordId,
  ENTITY_TYPE_ROUTE_MAP,
  FIELD_MAPPINGS,
  findClaimingRow,
  finishedGoodSyncTarget,
  fullDecisionBody,
  getEnabledEntityRoutes,
  isClaimedAivorOnlyRow,
  isExactUomSource,
  isManualUomSource,
  isSageService,
  matchSageUnit,
  toUnitOfMeasureOption,
  ItemReportGroupOption,
  SageEntityTypeName,
  SageReconciliationMatch,
  UiMappingStatus,
  UnitOfMeasureOption,
  UomSource,
  getFieldValue,
  isKeptOutOfSage,
  isSyncDisabledError,
  sageErrorMessageFor,
  leavesOldSageRecord,
  loadErrorMessageFor,
  mapDecisionToUiStatus,
  markUnitSaved,
  matchReasonText,
  sameItemKind,
} from '../../../shared/services/sage-integration.model';

type FilterKey = 'all' | 'review' | 'confirmed' | 'new-in-sage' | 'new-in-aivor';

interface AivorRecordLite {
  id: number;
  name: string;
  meta: string;
  raw: any;
}

interface RowViewModel {
  match: SageReconciliationMatch;
  sageName: string;
  sageMeta: string;
  sageRaw: any;
  aivorName: string;
  aivorMeta: string;
  aivorRaw: any;
  uiStatus: UiMappingStatus;
  confidence: number | null;
  matchReason: string | null;
  expanded: boolean;
  selectedUomId: number | null;
  uomResolved: boolean;
  uomSource: UomSource;
  selectedItemTypeId: number;
  componentRows: BOMComponentWithSageInfo[] | null;
  componentTableOpen: boolean;
  aivorMissing: boolean;
}

interface MappingFieldRow {
  aivorLabel: string;
  aivorValue: string;
  sageLabel: string;
  sageValue: string;
}

@Component({
  selector: 'app-sage-entity-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './sage-entity-detail.component.html',
  styleUrls: ['./sage-modal.shared.css', './sage-entity-detail.component.css'],
})
export class SageEntityDetailComponent implements OnInit {
  fullName = '';
  tenantName = '';

  entitySlug = '';
  entityType: SageEntityTypeName = 'Supplier';
  entityLabel = '';

  loading = true;
  loadError = false;
  loadErrorMessage = '';
  sessionId: number | null = null;
  rows: RowViewModel[] = [];
  aivorRecords: AivorRecordLite[] = [];
  // False when the list failed or the entity has none — then an unknown id isn't proof it's gone.
  aivorRecordsLoaded = false;
  uomOptions: UnitOfMeasureOption[] = [];
  itemReportGroups: ItemReportGroupOption[] = [];

  activeFilter: FilterKey = 'all';
  searchQuery = '';
  candidateSearchQuery = '';

  syncEnabled = true;
  applyingSession = false;
  pendingSyncTarget: { row: RowViewModel; target: 'Bundle' | 'Standalone' } | null = null;

  @ViewChild('reviewScroll') private reviewScroll?: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sageService: SageIntegrationService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.entitySlug = this.route.snapshot.paramMap.get('entityType') || '';
    const enabledRoutes = getEnabledEntityRoutes();
    const entityMeta = enabledRoutes.find((e) => e.slug === this.entitySlug);
    if (!entityMeta) {
      this.router.navigate(['/sage-integration']);
      return;
    }
    this.entityType = ENTITY_TYPE_ROUTE_MAP[this.entitySlug] || 'Supplier';
    this.entityLabel = entityMeta.label;

    this.loadEntity();
    this.sageService.getStatus().subscribe({
      next: (status) => (this.syncEnabled = status.syncEnabled !== false),
      error: () => {},
    });
  }

  retryLoad(): void {
    this.loadEntity();
  }

  private loadEntity(): void {
    this.loading = true;
    this.loadError = false;
    this.loadErrorMessage = '';

    forkJoin({
      // A 404 from getSession means no session exists yet — the expected first-run path, so
      // fall back to starting one. Any other error (auth/rate-limit/Sage outage, now classified
      // by the backend's SageApiException handling) is real and must not be masked behind a
      // second, doomed startReconciliation call.
      session: this.sageService.getSession(this.entityType).pipe(
        catchError((err: HttpErrorResponse) =>
          err.status === 404 ? this.sageService.startReconciliation(this.entityType) : throwError(() => err)
        ),
        catchError((err) => {
          this.loadError = true;
          this.loadErrorMessage = loadErrorMessageFor(err);
          return of(null);
        })
      ),
      aivorRecords: this.sageService.getAivorRecords(this.entityType).pipe(catchError(() => of(null))),
      uomOptions:
        this.entityType === 'Item'
          ? this.sageService.getUnitsOfMeasure().pipe(catchError(() => of([])))
          : of([]),
      itemReportGroups:
        this.entityType === 'Item'
          ? this.sageService.getItemReportGroups().pipe(catchError(() => of([])))
          : of([]),
    }).subscribe(({ session, aivorRecords, uomOptions, itemReportGroups }: any) => {
      this.loading = false;
      if (this.loadError || session === null) {
        return;
      }
      this.sessionId = session.session?.id ?? session.sessionId ?? null;
      const matches: SageReconciliationMatch[] = session.matches || [];
      this.aivorRecordsLoaded = aivorRecords !== null;
      this.aivorRecords = (aivorRecords || []).map((r: any) => this.toAivorRecordLite(r));
      this.uomOptions = (uomOptions || []).map(toUnitOfMeasureOption);
      this.itemReportGroups = (itemReportGroups || []).map((g: any) => this.toItemReportGroupOption(g));
      this.rows = matches.map((m) => this.toRowViewModel(m));
      if (this.entityType === 'Item') {
        this.applyDefaultUomSelections();
      }
    });
  }

  private toItemReportGroupOption(g: any): ItemReportGroupOption {
    const id = g.id ?? g.ID ?? 0;
    const sageId = g.sageItemReportingGroupId ?? g.SageItemReportingGroupId ?? null;
    const name = g.description ?? g.Description ?? g.name ?? g.Name ?? '';
    return { id, sageId, name };
  }

  /** Pre-selects each Item row's UOM dropdown to what the backend's own matching would pick, so it starts on "what it thinks it is." */
  private applyDefaultUomSelections(): void {
    for (const row of this.rows) {
      if (row.match.resolvedUOMID) {
        row.selectedUomId = row.match.resolvedUOMID;
        row.uomSource = this.savedUomSource(row);
        continue;
      }
      const sageUnit = this.sageUnitFor(row).trim().toLowerCase();
      if (!sageUnit || sageUnit === '—') continue;
      const unitMatch = matchSageUnit(sageUnit, this.uomOptions);
      if (unitMatch) {
        row.selectedUomId = unitMatch.option.id;
        row.uomSource = unitMatch.kind === 'exact' ? 'exact' : 'near';
      } else {
        const guess = this.uomOptions.find(
          (u) => sageUnit.includes(u.code.trim().toLowerCase()) || sageUnit.includes(u.name.trim().toLowerCase())
        );
        row.selectedUomId = guess?.id ?? this.uomOptions[0]?.id ?? null;
        row.uomSource = 'guess';
      }
    }
  }

  /** A saved unit that is Sage's own spelled another way reads as a near match, whoever saved it. */
  private savedUomSource(row: RowViewModel): UomSource {
    const unitMatch = matchSageUnit(this.sageUnitFor(row), this.uomOptions);
    if (unitMatch?.kind === 'equivalent' && unitMatch.option.id === row.match.resolvedUOMID) return 'near';
    return isManualUomSource(row.match.uomResolutionSource) ? 'manual' : 'exact';
  }

  private toAivorRecordLite(r: any): AivorRecordLite {
    const id = aivorRecordIdFor(this.entityType, r) ?? 0;
    return { id, ...aivorRecordDisplay(this.entityType, r), raw: r };
  }

  private parseSnapshotRaw(snapshot: string | null | undefined): any {
    if (!snapshot) return null;
    try {
      return JSON.parse(snapshot);
    } catch {
      return null;
    }
  }

  private toRowViewModel(match: SageReconciliationMatch): RowViewModel {
    const sageRaw = this.parseSnapshotRaw(match.sageRecordSnapshot);
    let sageName = sageRaw ? sageRaw.Name || sageRaw.Description || sageRaw.name || '—' : '—';
    let sageMeta = sageRaw ? [sageRaw.Code, sageRaw.Email].filter(Boolean).join(' · ') : '';
    if (this.entityType === 'ItemBundle' && sageRaw) {
      sageName = sageRaw.Description || sageRaw.BundleCode || '—';
      const componentCount = sageRaw.ItemBundleItems?.length ?? 0;
      sageMeta = `${sageRaw.BundleCode ?? ''} · ${componentCount} component${componentCount === 1 ? '' : 's'}`;
    }
    const aivorId = effectiveAivorRecordId(match);
    const aivorRecord = this.aivorRecords.find((r) => r.id === aivorId);
    const aivorMissing = !!aivorId && !aivorRecord && this.aivorRecordsLoaded;
    return {
      match,
      sageName: match.sageRecordId ? sageName : '— no Sage record —',
      sageMeta: match.sageRecordId ? sageMeta : 'will be created in Sage',
      sageRaw,
      aivorName:
        aivorRecord?.name ||
        (aivorMissing ? `Aivor record #${aivorId} not found` : aivorId ? `#${aivorId}` : '— no AIVOR record —'),
      aivorMeta:
        aivorRecord?.meta ||
        (aivorMissing
          ? 'It may have been deleted — pick another match or create as new'
          : aivorId
            ? ''
            : // A held possible duplicate: confirming it with nothing linked creates nothing.
              match.decision === 'Pending'
              ? 'Not linked — Confirm leaves it out, or Find match to link or create it'
              : 'will be created in AIVOR'),
      aivorRaw: aivorRecord?.raw ?? null,
      uiStatus: mapDecisionToUiStatus(match.decision),
      confidence: match.confidenceScore,
      matchReason: matchReasonText(match),
      expanded: false,
      selectedUomId: null,
      uomResolved: false,
      uomSource: 'guess',
      selectedItemTypeId: match.resolvedItemTypeID ?? 1,
      componentRows: null,
      componentTableOpen: false,
      aivorMissing,
    };
  }

  aivorIdOf(row: RowViewModel): number | null {
    return effectiveAivorRecordId(row.match);
  }

  needsUomMapping(row: RowViewModel): boolean {
    return row.match.decision === 'NeedsUomMapping';
  }

  /** Bundle/Standalone toggle (SAGE.BDL.012) — only rendered for rows with an AIVOR counterpart;
   * create-in-AIVOR rows have no toggle since the target defaults on creation. */
  syncTarget(row: RowViewModel): 'Bundle' | 'Standalone' | null {
    return row.aivorRaw ? finishedGoodSyncTarget(row.aivorRaw) : null;
  }

  /** Item type control (§2) — persisted via the same decision-update endpoint used for confirming
   * matches, since ResolvedItemTypeID already lives on UpdateMatchDecisionRequest. Never changes
   * the decision itself. */
  setItemType(row: RowViewModel, itemTypeId: number): void {
    if (!this.sessionId || row.selectedItemTypeId === itemTypeId) return;
    const previous = row.selectedItemTypeId;
    row.selectedItemTypeId = itemTypeId;
    this.sageService
      .updateMatchDecision(
        this.sessionId,
        row.match.id,
        fullDecisionBody(row.match, { resolvedAivorRecordId: this.aivorIdOf(row), resolvedItemTypeID: itemTypeId })
      )
      .subscribe({
        next: () => {
          row.match.resolvedItemTypeID = itemTypeId;
        },
        error: () => {
          row.selectedItemTypeId = previous;
          this.toast.error('Could not update the item type. Please try again.');
        },
      });
  }

  /** A Sage service always comes in as an Aivor service, so there's no item type to choose. */
  isSageServiceRow(row: RowViewModel): boolean {
    return this.entityType === 'Item' && isSageService(row.sageRaw);
  }

  itemTypeHint(row: RowViewModel): string {
    return row.selectedItemTypeId === 1
      ? 'Items from Sage come in as raw materials unless you choose otherwise'
      : 'How Aivor treats this item once it comes in';
  }

  uomQualifier(row: RowViewModel): string {
    switch (row.uomSource) {
      case 'exact':
      case 'near':
        return 'matched';
      case 'manual':
        return 'set manually';
      default:
        return 'check this';
    }
  }

  uomHint(row: RowViewModel): string {
    const sageUnit = this.sageUnitFor(row);
    switch (row.uomSource) {
      case 'exact':
        return `Exact match on Sage "${sageUnit}"`;
      case 'near':
        return `Same unit as Sage "${sageUnit}", spelled differently`;
      case 'manual':
        return `Chosen by you — Sage sends "${sageUnit}"`;
      default:
        return `Closest guess from Sage "${sageUnit}"`;
    }
  }

  /** AIVOR-side UOM value shown in the Sage record / AIVOR record columns — the resolved value, not the raw Sage unit. */
  selectedUomLabel(row: RowViewModel): string {
    const uom = this.uomOptions.find((u) => u.id === row.selectedUomId);
    return uom ? `${uom.code} — ${uom.name}` : '—';
  }

  syncTargetHint(row: RowViewModel): string {
    if (this.syncTarget(row) === 'Standalone') {
      return 'Sage gets one item, with the cost and price of its bill of materials added together';
    }
    const n = row.aivorRaw?.bundleInfo?.componentCount ?? 0;
    return `Sage gets a bundle that lists the ${n} item${n === 1 ? '' : 's'} it's made from`;
  }

  /** Collapsing the row also collapses its component table (spec §4). */
  toggleComponentTable(row: RowViewModel): void {
    row.componentTableOpen = !row.componentTableOpen;
    if (row.componentTableOpen && row.componentRows === null) {
      const itemId = row.aivorRaw?.itemID ?? row.aivorRaw?.ItemID;
      if (!itemId) {
        row.componentRows = [];
        return;
      }
      this.sageService.getComponentsWithSageInfo(itemId).subscribe({
        next: (result) => (row.componentRows = result.components || []),
        error: () => (row.componentRows = []),
      });
    }
  }

  setSyncTarget(row: RowViewModel, target: 'Bundle' | 'Standalone'): void {
    if (!row.aivorRaw || this.syncTarget(row) === target) return;
    const raw = row.aivorRaw;
    if (leavesOldSageRecord(this.syncTarget(row), target, raw.sageItemId ?? raw.SageItemId, raw.sageBundleId ?? raw.SageBundleId)) {
      this.pendingSyncTarget = { row, target };
      return;
    }
    this.saveSyncTarget(row, target);
  }

  confirmSyncTarget(): void {
    const pending = this.pendingSyncTarget;
    this.pendingSyncTarget = null;
    if (pending) this.saveSyncTarget(pending.row, pending.target);
  }

  cancelSyncTarget(): void {
    this.pendingSyncTarget = null;
  }

  private saveSyncTarget(row: RowViewModel, target: 'Bundle' | 'Standalone'): void {
    const itemId = row.aivorRaw.itemID ?? row.aivorRaw.ItemID;
    this.sageService.updateSyncTarget(itemId, target).subscribe({
      next: () => {
        row.aivorRaw.syncTargetType = target;
      },
      error: () => this.toast.error('Could not save how this is sent to Sage. Please try again.'),
    });
  }

  /** Dependency guard (SAGE.BDL.007/010) — derived, never stored, so it clears automatically as
   * Items get confirmed. Only meaningful for bundle rows with an AIVOR counterpart. */
  bundleBlockedWarning(row: RowViewModel): string | null {
    if (this.entityType !== 'ItemBundle' || !row.aivorRaw) return null;
    const blocked = row.aivorRaw.bundleInfo?.blockedComponentCount ?? 0;
    if (blocked === 0) return null;
    return blocked === 1
      ? "1 item it's made from isn't in Sage yet. Finish Items first"
      : `${blocked} items it's made from aren't in Sage yet. Finish Items first`;
  }

  sageUnitFor(row: RowViewModel): string {
    return row.sageRaw?.Unit || row.sageRaw?.unit || '—';
  }

  // Reads the name directly rather than resolving an ID — confirmed against real Sage data that
  // Item/Get never sends ItemReportingGroupId_Sales, only ItemReportingGroupSalesName, and that
  // Sage's built-in default groups (e.g. "Sales") never appear in the local ItemReportGroups
  // cache at all (only custom groups a company has created do), so an ID-based lookup can't work.
  sageReportGroupFor(row: RowViewModel): string {
    return row.sageRaw?.ItemReportingGroupSalesName || row.sageRaw?.ItemReportingGroupPurchasesName || '—';
  }

  /** Label for how the row's UOM was resolved — null while it's still just an unpersisted
   * client-side guess from applyDefaultUomSelections, not yet a real backend resolution. */
  uomResolutionLabel(row: RowViewModel): string | null {
    if (isExactUomSource(row.match.uomResolutionSource)) return 'Auto-matched';
    if (isManualUomSource(row.match.uomResolutionSource)) return 'Matched by you';
    return null;
  }

  resolveUomForRow(row: RowViewModel): void {
    if (!this.sessionId || !row.selectedUomId) return;
    this.sageService
      .resolveUom(this.sessionId, row.match.id, { purchaseUOMID: row.selectedUomId })
      .subscribe({
        next: () => {
          markUnitSaved(row.match, row.selectedUomId!);
          row.uomSource = this.savedUomSource(row);
          row.uomResolved = true;
          row.uiStatus = mapDecisionToUiStatus(row.match.decision);
        },
        error: () => this.toast.error('Could not save the unit. Please try again.'),
      });
  }

  /** Field-by-field AIVOR<->Sage comparison shown in the expanded row detail panel. */
  mappingRowsFor(row: RowViewModel): MappingFieldRow[] {
    const mappings = FIELD_MAPPINGS[this.entityType] || [];
    return mappings.map((m) => ({
      aivorLabel: m.aivorLabel,
      aivorValue: this.formatFieldValue(getFieldValue(row.aivorRaw, m.aivorKeys)),
      sageLabel: m.sageLabel,
      sageValue: this.formatFieldValue(getFieldValue(row.sageRaw, m.sageKeys)),
    }));
  }

  private formatFieldValue(value: any): string {
    if (value === undefined || value === null || value === '') return '—';
    return String(value);
  }

  get filterCounts(): Record<FilterKey, number> {
    const rows = this.visibleRows;
    const counts: Record<FilterKey, number> = { all: rows.length, review: 0, confirmed: 0, 'new-in-sage': 0, 'new-in-aivor': 0 };
    for (const r of rows) {
      if (r.uiStatus === 'review') counts.review++;
      if (r.uiStatus === 'confirmed') counts.confirmed++;
      if (r.uiStatus === 'new-in-sage') counts['new-in-sage']++;
      if (r.uiStatus === 'new-in-aivor') counts['new-in-aivor']++;
    }
    return counts;
  }

  // An Aivor-only row whose record a Sage row has claimed is that pairing, not something to create.
  get visibleRows(): RowViewModel[] {
    return this.rows.filter((r) => !isClaimedAivorOnlyRow(r, this.rows));
  }

  get filteredRows(): RowViewModel[] {
    let filtered = this.visibleRows;
    if (this.activeFilter !== 'all') {
      filtered = filtered.filter((r) => r.uiStatus === this.activeFilter);
    }
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (r) => r.sageName.toLowerCase().includes(q) || r.aivorName.toLowerCase().includes(q)
      );
    }
    return filtered;
  }

  get reviewRemaining(): number {
    return this.visibleRows.filter((r) => r.uiStatus === 'review').length;
  }

  get overallProgressPercent(): number {
    const total = this.visibleRows.length;
    if (total === 0) return 100;
    return Math.round(((total - this.reviewRemaining) / total) * 100);
  }

  setFilter(filter: FilterKey): void {
    this.activeFilter = filter;
  }

  toggleExpand(row: RowViewModel): void {
    const wasExpanded = row.expanded;
    this.rows.forEach((r) => {
      r.expanded = false;
      r.componentTableOpen = false;
    });
    row.expanded = !wasExpanded;
    this.candidateSearchQuery = '';
    if (row.expanded) this.revealExpandedCard();
  }

  // On a phone the chips scroll sideways in one row; keep the chosen one in view.
  revealChip(event: Event): void {
    const chip = event.currentTarget as HTMLElement;
    const strip = chip.parentElement;
    if (!strip) return;
    const left = strip.scrollLeft + chip.getBoundingClientRect().left - strip.getBoundingClientRect().left - 16;
    strip.scrollTo({ left, behavior: this.scrollBehavior() });
  }

  // On a phone the list scrolls in its own area above the pinned footer, so an opened card near
  // the bottom would hide its details behind the footer. On desktop the area isn't a scroller.
  private revealExpandedCard(): void {
    // After the details panel has rendered.
    setTimeout(() => {
      const scroller = this.reviewScroll?.nativeElement;
      const card = scroller?.querySelector<HTMLElement>('.body-row.expanded');
      const details = card?.nextElementSibling;
      if (!scroller || !card || !details || scroller.scrollHeight <= scroller.clientHeight) return;
      const view = scroller.getBoundingClientRect();
      if (details.getBoundingClientRect().bottom <= view.bottom) return;
      scroller.scrollTo({ top: scroller.scrollTop + card.getBoundingClientRect().top - view.top - 16, behavior: this.scrollBehavior() });
    });
  }

  private scrollBehavior(): ScrollBehavior {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  readonly candidateLimit = 8;

  candidateMatches(row: RowViewModel): AivorRecordLite[] {
    const currentId = this.aivorIdOf(row);
    const q = this.candidateSearchQuery.trim().toLowerCase();
    return this.aivorRecords
      .filter((r) => r.id !== currentId)
      .filter((r) => this.entityType !== 'Item' || (sameItemKind(row.sageRaw, r.raw) && !isKeptOutOfSage(r.raw)))
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.meta.toLowerCase().includes(q));
  }

  candidateOptions(row: RowViewModel): AivorRecordLite[] {
    return this.candidateMatches(row).slice(0, this.candidateLimit);
  }

  confirmRow(row: RowViewModel): void {
    if (!this.sessionId) return;
    this.sageService
      .updateMatchDecision(
        this.sessionId,
        row.match.id,
        fullDecisionBody(row.match, { decision: 'Confirmed', resolvedAivorRecordId: row.match.aivorRecordId })
      )
      .subscribe({
        next: () => {
          row.match.decision = 'Confirmed';
          row.match.resolvedAivorRecordId = row.match.aivorRecordId;
          row.uiStatus = 'confirmed';
          row.confidence = 100;
        },
        error: () => this.toast.error('Could not confirm this match. Please try again.'),
      });
  }

  claimedBy(row: RowViewModel, candidate: AivorRecordLite): string | null {
    return findClaimingRow(this.rows, candidate.id, row.match.id)?.sageName ?? null;
  }

  selectCandidate(row: RowViewModel, candidate: AivorRecordLite): void {
    if (!this.sessionId || this.claimedBy(row, candidate)) return;
    this.sageService
      .updateMatchDecision(
        this.sessionId,
        row.match.id,
        fullDecisionBody(row.match, { decision: 'Confirmed', resolvedAivorRecordId: candidate.id })
      )
      .subscribe({
        next: () => {
          row.match.decision = 'Confirmed';
          row.match.resolvedAivorRecordId = candidate.id;
          row.aivorName = candidate.name;
          row.aivorMeta = candidate.meta;
          row.aivorRaw = candidate.raw;
          row.aivorMissing = false;
          row.uiStatus = 'confirmed';
          row.confidence = 100;
          row.expanded = false;
        },
        error: () => this.toast.error('Could not save this match. Please try again.'),
      });
  }

  createAsNew(row: RowViewModel): void {
    if (!this.sessionId) return;
    this.sageService
      .updateMatchDecision(
        this.sessionId,
        row.match.id,
        fullDecisionBody(row.match, { decision: 'ImportToAivor', resolvedAivorRecordId: null })
      )
      .subscribe({
        next: () => {
          row.match.decision = 'ImportToAivor';
          row.match.resolvedAivorRecordId = null;
          row.aivorName = '— no AIVOR record —';
          row.aivorMeta = 'will be created in AIVOR';
          row.aivorRaw = null;
          row.aivorMissing = false;
          row.uiStatus = 'new-in-aivor';
          row.confidence = null;
          row.expanded = false;
        },
        error: () => this.toast.error('Could not create this record. Please try again.'),
      });
  }

  /** Same action as the console review queue's "Apply" button — confirms the session and runs the sync. */
  applySession(): void {
    if (!this.fromConsole || !this.sessionId || this.applyingSession || !this.syncEnabled) return;
    this.applyingSession = true;
    this.sageService.confirmSession(this.sessionId).subscribe({
      next: (result) => {
        this.applyingSession = false;
        const carried = result.carriedForwardCount ?? 0;
        this.toast.success(
          carried > 0
            ? `${this.entityLabel} applied — ${carried} record${carried === 1 ? '' : 's'} still need attention.`
            : `${this.entityLabel} applied.`
        );
        this.goBack();
      },
      error: (err) => {
        this.applyingSession = false;
        if (isSyncDisabledError(err)) {
          this.syncEnabled = false;
          this.toast.error(err.error.message || 'Sage sync is paused.');
          return;
        }
        this.toast.error(sageErrorMessageFor(err) ?? `Could not apply ${this.entityLabel}. Please try again.`);
      },
    });
  }

  confirmAllShown(): void {
    if (!this.sessionId) return;
    const targets = this.filteredRows.filter((r) => r.uiStatus === 'review' && !this.needsUomMapping(r) && !r.aivorMissing);
    targets.forEach((row) => this.confirmRow(row));
  }

  // During onboarding, sending to Sage is the wizard's "Run first sync": applying record types one
  // by one would complete onboarding early and skip it. Reviewers from the console are past that.
  get fromConsole(): boolean {
    return this.route.snapshot.queryParamMap.get('from') === 'console';
  }

  goBack(): void {
    if (this.fromConsole) {
      this.router.navigate(['/sage-integration/console']);
      return;
    }
    this.router.navigate(['/sage-integration/overview'], { queryParams: { step: this.entitySlug } });
  }
}
